import { UserValue } from '../models/user/UserValue';
import { UserLoginResponse } from '../models/user/UserLoginResponse';
import {
  sendFormDataClient,
  sendJsonPayloadClient,
  isAxiosError,
  unexpectedStatusCodeMessage,
} from './api-main';

export const errorMessages: Record<number, string> = {
  404: 'Incorrect username / password combination.',
  403: 'Incorrect username / password combination.',
  500: 'Server error while logging user in.',
  504: 'Server is not responding. Please try again later.',
};

const knownErrorCodes = Object.keys(errorMessages).map((k) => parseInt(k));

export const getUserLogin = async (
  userName: string,
  password: string,
): Promise<UserLoginResponse> => {
  try {
    const { data } = await sendJsonPayloadClient.post('/user/login', {
      username: userName,
      password: password,
    });
    return { token: data, success: true };
  } catch (error) {
    if (!isAxiosError(error)) {
      return {
        token: '',
        success: false,
        errorMessage: `Unexpected error while logging user in: ${error}`,
      };
    }

    // else is an axios error
    const myErrorCode: number = error.response?.status || 0;
    let errorMessage =
      unexpectedStatusCodeMessage(myErrorCode) +
      ` while logging user in: ${error}`;
    if (knownErrorCodes.includes(myErrorCode)) {
      errorMessage = errorMessages[error.response?.status as number];
    }
    return { token: '', success: false, errorMessage };
  }
};

export const getAllUsers = async (token: string): Promise<UserValue[]> => {
  try {
    const { data } = await sendFormDataClient.get(
      `/user/getUsers?token=${token}`,
    );
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getCurrentUser = async (
  token: string,
): Promise<UserValue | null> => {
  try {
    const { data } = await sendFormDataClient.get(
      `/user/getUser?token=${token}&userPk=`,
    );
    console.debug('getCurrentUser', data);
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const addUser = async (
  token: string,
  username: string,
  password: string,
  email = '',
  active = true,
  admin = false,
): Promise<boolean> => {
  try {
    const response = await sendJsonPayloadClient.post('/user/addUser', {
      token,
      login: username,
      password,
      email,
      active,
      admin,
    });
    return response.status === 200;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const deleteUser = async (
  token: string,
  userPk: string,
): Promise<boolean> => {
  try {
    const response = await sendFormDataClient.post(
      `/user/deleteUser?token=${token}&userPk=${userPk}`,
    );
    return response.status === 200;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const logoutUser = async (token: string): Promise<boolean> => {
  try {
    const response = await sendFormDataClient.get(
      `/user/logout?securityToken=${token}`,
    );
    return response.status == 200;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const updateUser = async (
  token: string,
  userPk: string,
  username: string,
  password: string,
  email: string,
  active: boolean,
  admin: boolean,
): Promise<boolean> => {
  try {
    const response = await sendJsonPayloadClient.post('/user/editUser', {
      token,
      userPk,
      login: username,
      password,
      email,
      active,
      admin,
    });
    return response.status === 200;
  } catch (error) {
    console.error(error);
    return false;
  }
};
